import React, { ReactElement } from "react";
import { Text, Modal, useDisclosure, Shimmer } from "sprint-common-web";

import { SprintAccessRight } from "../../core/security/SprintAccessRight";
import { NoteItem } from "../components/notes/hooks/state/NoteItem";
import { sortNotesDescending } from "../components/notes/hooks/useNotesActions";
import { Notes } from "../components/notes/Notes";
import { NoteSubjectType } from "../types/NoteSubjectType";

const CHARACTER_LIMIT = 100;

interface RenderNotesProps {
    itemId?: string;
    notes?: NoteItem[];
    subjectType: NoteSubjectType;
    isLoading?: boolean;
    shortPreview?: boolean;
    renderBadge?(noteItem: NoteItem): JSX.Element;
}

export const renderNotes = ({
    itemId,
    notes,
    subjectType,
    renderBadge,
    isLoading = false,
    shortPreview = false
}: RenderNotesProps): ReactElement | null => {
    const { isOpen, onClose, onOpen } = useDisclosure();

    if (isLoading) {
        return <Shimmer />;
    }

    if (!notes?.length || !itemId) {
        return <Text value={"-"} />;
    }

    const latestNoteContent = getLatestNoteContent(notes);

    return (
        <>
            <NotesModal
                isOpen={isOpen}
                itemId={itemId}
                notes={notes}
                onClose={onClose}
                subjectType={subjectType}
                renderBadge={renderBadge}
            />
            <Text variant={"small"} value={latestNoteContent} styles={{ root: { whiteSpace: "normal" } }} />
            {!shortPreview && (
                <Text
                    value="Show more"
                    onClick={onOpen}
                    styles={{ root: { fontWeight: 600, textDecoration: "underline", cursor: "pointer" } }}
                />
            )}
        </>
    );
};

interface NotesModalProps {
    isOpen: boolean;
    notes: NoteItem[];
    subjectType: NoteSubjectType;
    itemId: string;
    onClose(): void;
    renderBadge?(noteItem: NoteItem): JSX.Element;
}

const NotesModal = ({ onClose, isOpen, itemId, notes, subjectType, renderBadge }: NotesModalProps) => {
    return (
        <Modal onDismiss={onClose} isOpen={isOpen} key={itemId} headerText="Notes" displayCloseButton>
            <Notes
                subjectType={subjectType}
                defaultNotes={notes}
                subjectId={itemId}
                sourceIds={[itemId]}
                style={{ boxShadow: "none" }}
                isHeader={false}
                accessRights={[SprintAccessRight.CiReadWrite]}
                renderBadge={renderBadge}
            />
        </Modal>
    );
};

const getLatestNoteContent = (notes: NoteItem[]): string => {
    const sortedNotes = sortNotesDescending(notes);

    return getNoteFormattedContent(sortedNotes[0]);
};

const getNoteFormattedContent = (item: NoteItem) => {
    return item.content.length > CHARACTER_LIMIT ? item.content.substring(0, CHARACTER_LIMIT) + "..." : item.content;
};
